import React, {useState, useEffect} from 'react';
import hash from 'object-hash';
import { cloneDeep } from 'lodash';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import Loading from "../../../Global/Loading";
import CertificatePrintWrapper, { printCertificates } from './CertificatePrintWrapper';
import PrintModal from './PrintModal';
import getTeacherName from '../../util/getTeacherName';
import badgingActions from '../../state/badging/actions';
import { updateBadge } from '../../../../services/badging/updateBadge/updateBadge';
import PromisePool from '@supercharge/promise-pool/dist';
import BadgingTable from './BadgingTable';
import BadgingControls from './BadgingControls';
import getAwardablePrintableBadgesFromSelection from './getAwardablePrintableBadgesFromSelection';
import BadgingTableHeader from './BadgingTableHeader';
import exportToCsv from '../../util/exportToCsv';
import BadgingUsageChart from './BadgingUsageChart';
import BadgingNotification from './BadgingNotification'

export default function BadgingContent(props) {
  const {status, reportData, fetchReport, usageWindow, badgingLocale} = props;

  const [selectedRows, setSelectedRows] = useState([]); // [studentId]
  const [selectedCells, setSelectedCells] = useState({}); // {studentId: [BADGE_TYPE]}
   // printModalStage is enum 0, 1, 2
   // 0: modal is not visible
   // 1: modal asks user to confirm awarding badges prior to printing
   // 2: modal asks users whether to print in color or monochrome
  const [printModalStage, setPrintModalStage] = useState(0);
  const [waiting, setWaiting] = useState(false);

  const dispatch = useDispatch();
  const setBadgingReduxState = state => dispatch(badgingActions.setState(state));

  const {t} = useTranslation("badging_report");

  useEffect(() => {
    // on report load...
    if (status === 'DONE') {
      // select all rows
      setSelectedRows(reportData.map(x => x.studentId));
      selectAllAwardableBadges();
    }
  }, [status]);

  if (status === "ERROR")
    return (
      <div className="filler-text">
        Error: unable to retrieve badging report.
      </div>
    );
    
  if (!status || status === "PENDING")
    return (
      <div style={{ height: 500 }}>
        <Loading />
      </div>
    );
    
  const teacherName = getTeacherName();
  const { awardableBadges, printableBadges } = getAwardablePrintableBadgesFromSelection(reportData, selectedCells, teacherName);
  const allAwardableBadgesForClass = reportData.flatMap(row => row.allAwardableBadges);
  const isTableChecked = reportData.length === selectedRows.length;

  const handleExportCsv = () => {
    const data = [['First Name', 'Last Name', 'Tutoring Time', 'Stories Read']]
    for (const row of reportData) {
      data.push([row.firstName, row.lastName, row.sessionTime, row.activityCount])
    }
    const filename = `Amira Usage ${usageWindow.labelStart}-${usageWindow.labelEnd}`
    exportToCsv(data, filename)
  }

  const awardBadges = async (badges) => {
    const payloads = [];
    for (const badge of badges) {
      const {studentId, id} = badge;
      payloads.push({
        studentId,
        badgeId: id,
        awarded: true,
        awarder: teacherName,
      })
    }

    setWaiting(true);
    const { errors } = await PromisePool.for(payloads).process(updateBadge);
    if (errors.length > 0) {console.error('Badge update errors', errors);}
    
    // revalidate report data
    await fetchReport({skipLoading: true});
    setWaiting(false);
  }

  const handleAwardBadges = () => {
    // award all award badges within current selection and usage window
    awardBadges(awardableBadges);
  }

  const handleAwardAllBadges = () => {
    // award all awardable badges, regardless of usage window
    awardBadges(allAwardableBadgesForClass);
  }

  return (
    <>
      <div className='badgingReportBody'>
        {allAwardableBadgesForClass.length > 0 && (
          <BadgingNotification onClickButton={handleAwardAllBadges} buttonText={t("Award All")} waiting={waiting}>
            {t("newBadgeNotification", {count: allAwardableBadgesForClass.length})}
          </BadgingNotification>
        )}
        <BadgingUsageChart usageWindow={usageWindow} reportData={reportData} />
        <BadgingTableHeader usageWindow={usageWindow} onExportCsv={handleExportCsv} />
        <div className='badgingTableWrapper'>
          <BadgingTable 
            reportData={reportData} 
            selectedRows={selectedRows} 
            selectedCells={selectedCells} 
            isTableChecked={isTableChecked} 
            toggleTable={toggleTable}
            toggleColumn={toggleColumn}
            toggleRow={toggleRow}
            toggleCell={toggleCell}
            badgingLocale={badgingLocale}
          />
        </div>
        <BadgingControls
          awardableBadges={awardableBadges}
          printableBadges={printableBadges}
          selectedRows={selectedRows}
          onAwardBadges={handleAwardBadges}
          onPrintBadges={onPrintBadges}
          buildCertificates={buildCertificates}
          waiting={waiting}
        />
      </div>
      <CertificatePrintWrapper badges={printableBadges} />
      <PrintModal 
        onClose={() => setPrintModalStage(0)}
        onConfirm={onConfirmPrintModal}
        onSubmit={onSubmitPrintModal}
        stage={printModalStage}
      />
    </>
  );

  // handles selecting or de-selecting a badge icon
  function toggleCell(studentId, badgeTypeId) {
    const update = cloneDeep(selectedCells);
    if (!(studentId in update)) {update[studentId] = [];}
    const studentCellSelection = update[studentId];
    const index = studentCellSelection.indexOf(badgeTypeId);
    if (index > -1) {studentCellSelection.splice(index, 1);}
    else {studentCellSelection.push(badgeTypeId);}
    setSelectedCells(update);
  }

  // handles selecting or de-selecting a student
  function toggleRow(studentId) {
    const update = Array.from(selectedRows);
    const cellsUpdate = cloneDeep(selectedCells);
    const index = update.indexOf(studentId);
    if (index > -1) {
      // deselecting row
      update.splice(index, 1);
      cellsUpdate[studentId] = [];
    }
    else {
      // selecting row
      update.push(studentId);
      const student = reportData.find(x => x.studentId === studentId);
      const { badges } = student;
      cellsUpdate[studentId] = getAwardableBadgeTypes(badges);
    }
    setSelectedRows(update);
    setSelectedCells(cellsUpdate);
  }

  // handles selecting a de-selecting a badge type column
  function toggleColumn(badgeTypeId) {
    if (!badgeTypeId) return;
    const update = {};
    for (const student of reportData) {
      const { studentId, badges: badgesByType } = student;
      const badges = badgesByType[badgeTypeId] || [];
      if (badges.length > 0) update[studentId] = [badgeTypeId];
    }
    if (hash(update) === hash(selectedCells)) {setSelectedCells({});}
    else setSelectedCells(update);
  }

  function toggleTable() {
    if (isTableChecked) {
      // deselecting table...
      setSelectedRows([]);
      setSelectedCells({});
    }
    else {
      // selecting table...
      setSelectedRows(reportData.map(x => x.studentId));
      selectAllAwardableBadges();
    }
  }

  // Accepts {badgeTypeId: [Badge]} and returns [badgeTypeIds] of awardable badges
  function getAwardableBadgeTypes(badgesByType) {
    const awardable = [];
    for (const badgeTypeId in badgesByType) {
      const badges = badgesByType[badgeTypeId];
      if (badges.some(x => !x.awarded)) awardable.push(badgeTypeId);
    }
    return awardable;
  }

  // selects badge cells within the table that are awardable
  function selectAllAwardableBadges() {
    const update = {};
    for (const student of reportData) {
      const {studentId, badges} = student;
      update[studentId] = getAwardableBadgeTypes(badges);
    }
    setSelectedCells(update);
  }

  // on click "Print X" button
  function onPrintBadges() {
    // trigger print warning modal if any badges in print selection have not been awarded
    if (printableBadges.some(x => !x.awarded)) return setPrintModalStage(1);
    else setPrintModalStage(2);
  }

  // on click "Proceed" within the print warning modal
  async function onConfirmPrintModal() {
    const badgesToUpdate = []
    for (const badge of printableBadges) {
      const {studentId, id, awarded} = badge;
      if (awarded) continue;
      badgesToUpdate.push({
        studentId,
        badgeId: id,
        awarded: true,
        awarder: teacherName,
      })
    }

    const { errors } = await PromisePool.for(badgesToUpdate).process(updateBadge);
    if (errors.length > 0) console.error('Badge update errors', errors);
    setPrintModalStage(2);
  }

  function onSubmitPrintModal(isColorPrint) {
    printCertificates(isColorPrint);
    // refresh local state
    fetchReport({skipLoading: true});
    setPrintModalStage(0);
  }

  // on click "Build Certificates"
  function buildCertificates() {
    const studentDataById = {};
    for (const row of reportData) {
      const {firstName, lastName, studentId} = row;
      studentDataById[studentId] = {
        studentName: `${firstName} ${lastName}`,
        studentId,
      }
    }
    const students = cloneDeep(selectedRows.map(id => studentDataById[id]));
    setBadgingReduxState({ students, isClassBadge: isTableChecked });
    props.history.push('/teacher/reports/type/badge-builder');
  }
}